<template>
  <BCard>
    <div class="text-black text-[20px] mb-2 font-[600]">
      Organisasi
    </div>

    <!-- Filter Section -->
    <div class="flex flex-col md:flex-row items-center justify-between gap-2 mb-3">
      <div class="w-full md:w-1/4">
        <BInputGroup
          class="input-group-merge"
          size="md"
        >
          <BInputGroupPrepend is-text>
            <feather-icon icon="SearchIcon" />
          </BInputGroupPrepend>
          <BFormInput
            id="filterInput"
            v-model="search"
            :maxlength="32"
            :minlength="3"
            type="search"
            placeholder="Cari organisasi"
            debounce="500"
          />
        </BInputGroup>
      </div>
      <div class="w-full md:w-auto">
        <BButton
          v-b-modal.modal-add-organization
          variant="light"
          size="md"
          class="!flex items-center justify-center gap-2 !px-6 !py-3 !shadow !bg-white !border-[#E5E5E5] !rounded-lg"
        >
          <img
            src="https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/add.svg"
            alt="add"
            width="16"
            height="16"
          >
          <span>Organisasi</span>
        </BButton>
      </div>
    </div>

    <!-- Table Section -->
    <BOverlay
      variant="light"
      :show="isLoading"
      spinner-variant="primary"
      blur="0"
      opacity=".5"
      rounded="sm"
    >
      <div class="border rounded-lg overflow-hidden">
        <BTable
          :fields="fields"
          :items="items"
          responsive
          show-empty
          thead-class="bg-[#F9FAFB]"
          tbody-tr-class="hover:bg-slate-100 transition-colors duration-300 cursor-pointer"
        >
          <template #cell(no)="data">
            <div class="text-base max-w-[100px]">
              {{ (currentPage - 1) * totalPerPage + data.index + 1 }}
            </div>
          </template>

          <template #cell(name)="data">
            <div class="truncate max-w-[250px]">
              {{ data.item.name }}
            </div>
          </template>

          <template #cell(level)="data">
            <div
              v-if="data.item.user_level_icons && data.item.user_level_icons.length"
              class="text-base max-w-[250px] flex items-center"
            >
              <template v-for="(icon, index) in data.item.user_level_icons">
                <img
                  v-if="icon"
                  :key="`icon-${index}`"
                  :src="icon"
                  alt="level"
                  width="20"
                  class="mr-[5px]"
                >
                <span
                  v-else
                  :key="`dash-${index}`"
                >
                  -
                </span>
              </template>
            </div>
            <div
              v-else
              class="truncate max-w-[250px]"
            >
              -
            </div>
          </template>

          <template #cell(product)="data">
            <div class="text-base max-w-[250px] flex items-center">
              <div v-if="!data.item.product_icons || data.item.product_icons.length === 0">
                -
              </div>
              <div
                v-else
                class="flex flex-wrap"
              >
                <div
                  v-for="(icon, index) in data.item.product_icons"
                  :key="index"
                >
                  <div
                    v-if="icon"
                    class="w-[32px] h-[32px] flex items-center justify-center rounded-full border-[1px] border-muted shadow-sm bg-white object-contain"
                    :class="index > 0 ? '-ml-2' : ''"
                  >
                    <img
                      :src="icon"
                      alt="product"
                      :class="icon && icon.toLowerCase().includes('rajaongkir') ? 'w-[16px] h-[16px]' : 'w-[20px] h-[20px]'"
                    >
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template #cell(action)="data">
            <div class="flex justify-center items-center">
              <BDropdown
                variant="none"
                no-caret
                right
                toggle-class="p-0 bg-transparent border-0 flex items-center justify-center"
                size="sm"
              >
                <template #button-content>
                  <img
                    src="https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/more.svg"
                    alt="more-icon"
                    width="20"
                    height="20"
                    class="shrink-0 object-contain"
                  >
                </template>
                <div class="flex flex-col gap-[5px] p-[5px] w-[200px]">
                  <div
                    class="flex gap-[3px] items-center px-[4px] hover:bg-[#f8f8f8] text-[#626262] text-[14px] py-[5px] cursor-pointer"
                    @click="$router.push('/organization/' + data.item.id_org)"
                  >
                    <div>Lihat Detail</div>
                  </div>
                  <div
                    class="flex gap-[3px] items-center px-[4px] hover:bg-[#f8f8f8] text-[#626262] text-[14px] py-[5px] cursor-pointer"
                    @click="editNameOrganization(data.item)"
                  >
                    <div>Edit Nama</div>
                  </div>
                  <div
                    class="flex gap-[3px] items-center px-[4px] hover:bg-[#f8f8f8] text-[#DC2626] text-[14px] py-[5px] cursor-pointer"
                    @click="deleteOrganization(data.item)"
                  >
                    <div>Hapus</div>
                  </div>
                </div>
              </BDropdown>
            </div>
          </template>

          <template #empty>
            <div class="flex flex-col items-center justify-center py-[120px]">
              <p class="text-[#6B7280] text-[16px] mb-1">
                Tidak ada data
              </p>
              <div
                v-if="totalRows === 0 && !search"
                class="flex flex-col gap-[12px] items-center justify-center"
              >
                <p class="text-[#9CA3AF] text-[14px]">
                  Kamu belum membuat organisasi
                </p>
                <BButton
                  v-b-modal.modal-add-organization
                  variant="primary"
                  class="!bg-[#F95031] !border-[#F95031] !rounded-lg !px-5 !py-2"
                >
                  Tambah Organisasi
                </BButton>
              </div>
            </div>
          </template>
        </BTable>
      </div>
    </BOverlay>

    <!-- Pagination Section -->
    <BCardBody class="d-flex justify-content-between flex-wrap pt-0 mt-1">
      <BFormGroup
        label="Per Page"
        label-cols="6"
        label-align="left"
        label-size="sm"
        label-for="perPageSelect"
        class="text-nowrap mb-md-0 mr-1"
      >
        <BFormSelect
          id="perPageSelect"
          v-model="totalPerPage"
          size="sm"
          inline
          :options="optionsPage"
        />
      </BFormGroup>

      <div>
        <BPagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="totalPerPage"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
          class="mb-0"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </BPagination>
      </div>
    </BCardBody>

    <!-- Modals -->
    <ModalAdd @submit="onSubmitOrganization" />

    <ModalEdit
      :initial-data="selectedOrganization"
      @submit="handleConfirmEditOrganization"
    />

    <ModalDelete
      :initial-data="selectedOrganization"
      @submit="onDeleteConfirm"
    />
  </BCard>
</template>

<script>
import { newAxiosIns } from '@/libs/axios'
import { fieldOrganization } from './config'
import ModalAdd from './ModalAdd.vue'
import ModalEdit from './ModalEdit.vue'
import ModalDelete from './ModalDelete.vue'

export default {
  components: {
    ModalAdd,
    ModalEdit,
    ModalDelete,
  },
  data() {
    return {
      fields: fieldOrganization,
      items: [],
      selectedOrganization: null,
      search: '',

      // State
      isLoading: false,

      // Pagination
      currentPage: 1,
      totalPerPage: 20,
      totalRows: 0,
      optionsPage: [20, 50, 75],
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRows / this.totalPerPage))
    },
  },
  watch: {
    currentPage() {
      this.getListOrganization()
    },
    totalPerPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.currentPage === 1) {
          this.getListOrganization()
        } else {
          this.currentPage = 1
        }
      }
    },
    search(newVal) {
      if (newVal && newVal.length > 0 && newVal.length < 3) {
        return
      }

      if (this.currentPage === 1) {
        this.getListOrganization()
      } else {
        this.currentPage = 1
      }
    },
  },
  mounted() {
    this.getListOrganization()
  },
  methods: {
    async getListOrganization() {
      try {
        this.isLoading = true
        const url = '/auth/api/v1/organizations'
        const params = {
          search: this.search,
          page: this.currentPage,
          per_page: this.totalPerPage,
        }

        const { data } = await newAxiosIns.get(url, { params })
        this.items = data?.data || []
        this.totalRows = data?.meta?.pagination?.total || 0
      } catch (error) {
        this.$toast_error({ message: error?.response?.data?.message || error?.message || 'Gagal memuat daftar organisasi' })
      } finally {
        this.isLoading = false
      }
    },
    onSubmitOrganization() {
      this.$bvModal.hide('modal-add-organization')
      this.getListOrganization()
    },
    editNameOrganization(payload) {
      this.selectedOrganization = payload
      this.$bvModal.show('modal-edit-organization')
    },
    handleConfirmEditOrganization() {
      this.$bvModal.hide('modal-edit-organization')
      this.$toast_success({ message: 'Kamu berhasil memperbarui nama organisasi' })
      this.getListOrganization()
    },
    deleteOrganization(payload) {
      if (payload && Number(payload.total_accounts) > 0) {
        this.$toast_error({ message: 'Organisasi memiliki akun tertaut' })
        return
      }
      this.selectedOrganization = payload
      this.$bvModal.show('modal-delete-organization')
    },
    onDeleteConfirm() {
      this.$bvModal.hide('modal-delete-organization')
      this.getListOrganization()
    },
  },
}
</script>

<style scoped>

</style>
